import { Component } from '@theme/component';
import { ThemeEvents, SlideshowSelectEvent, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    document.addEventListener('purchase:image-update', this.#handlePurchaseImageUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#syncGalleryNav, { signal });
    document.addEventListener('engraving-preview:update', this.#handleEngravingPreview, { signal });
    document.addEventListener('product-media:select', this.#handleImmediateMediaSelect, { signal });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#bindGalleryNavSync();
        this.#selectSelectedProductMedia();
        this.#syncEngravingPreviewFromSelector();
      });
    });
  }

  #bindGalleryNavSync() {
    const slideshow = this.slideshow;
    if (!slideshow) return;

    const { signal } = this.#controller;
    const scroller = slideshow.refs?.scroller;

    scroller?.addEventListener('scroll', this.#syncGalleryNav, { signal, passive: true });
    this.#syncGalleryNav();
  }

  #getGalleryNavButtons() {
    const slideshow = this.slideshow;
    if (!slideshow) return { previous: null, next: null };

    return {
      previous: slideshow.querySelector('.product-media-gallery__nav--prev'),
      next: slideshow.querySelector('.product-media-gallery__nav--next'),
    };
  }

  #controller = new AbortController();
  #engravingPreviewFitFrame = 0;
  #engravingPreviewResizeObserver = new ResizeObserver(() => {
    const previewText = this.querySelector('[data-engraving-preview-text]');
    if (previewText instanceof HTMLElement) this.#scheduleEngravingPreviewFit(previewText);
  });

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#engravingPreviewResizeObserver.disconnect();
    cancelAnimationFrame(this.#engravingPreviewFitFrame);
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    if (this.closest('.product-information--product-wallet')) {
      event.promise.catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Product update rejected:', error);
      });
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (this.closest('.product-information--product-wallet')) return;

    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Selects the carousel slide for the current purchase context (quantity + sub/OTP).
   *
   * @param {CustomEvent} event
   */
  #handlePurchaseImageUpdate = (event) => {
    const { mediaId, gallery } = event.detail ?? {};
    if (mediaId == null || mediaId === '') return;

    if (gallery && gallery !== this) return;

    const id = String(mediaId);
    const slideshow = this.querySelector('slideshow-component');
    if (!slideshow?.select) return;

    const attempt = () => {
      const slides = slideshow.refs?.slides?.length
        ? slideshow.refs.slides
        : Array.from(slideshow.querySelectorAll('slideshow-slide'));

      if (!slides.length) return false;

      for (let index = 0; index < slides.length; index++) {
        const slide = slides[index];
        if (!slide) continue;

        const slideId = slide.getAttribute('slide-id');
        if (slideId != null && slideId == id) {
          if (slide.hasAttribute('hidden')) {
            slide.setAttribute('reveal', '');
            slide.setAttribute('aria-hidden', 'false');
          }
          slideshow.select({ id }, undefined, { animate: false });
          return true;
        }

        if (slide.querySelector(`[data-media-id="${id}"]`)) {
          if (slide.hasAttribute('hidden')) {
            slide.setAttribute('reveal', '');
            slide.setAttribute('aria-hidden', 'false');
          }
          slideshow.select(index, undefined, { animate: false });
          return true;
        }
      }

      return false;
    };

    if (attempt()) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => attempt());
    });
  };

  #handleEngravingPreview = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const selector = event.detail?.selector;
    if (!(selector instanceof Element) || !this.#isSameProductSection(selector)) return;

    this.#renderEngravingPreview(event.detail);
  };

  #handleImmediateMediaSelect = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const selector = event.detail?.selector;
    if (!(selector instanceof Element) || !this.#isSameProductSection(selector)) return;

    const mediaId = String(event.detail?.mediaId || '');
    if (!mediaId) return;

    this.dataset.selectedMediaId = mediaId;
    if (this.dataset.engravingPreviewEnabled === 'true') return;

    const selectedIndex = this.#getProductMediaIndex(mediaId);
    if (selectedIndex < 0) return;

    const selectedSlide = this.slideshow?.slides?.[selectedIndex];
    const selectedImage = selectedSlide?.querySelector('.product-media__image');
    if (selectedImage instanceof HTMLImageElement) {
      selectedImage.loading = 'eager';
      selectedImage.fetchPriority = 'high';
    }

    Promise.resolve(this.slideshow?.select(selectedIndex, undefined, { animate: false })).then(this.#syncGalleryNav);
  };

  /** @param {Element} selector */
  #isSameProductSection(selector) {
    const galleryRoot = this.closest('.product-information, .shopify-section');
    const selectorRoot = selector.closest('.product-information, .shopify-section');
    return Boolean(galleryRoot && selectorRoot && galleryRoot === selectorRoot);
  }

  /** @param {string} mediaId */
  #getProductMediaIndex(mediaId) {
    const slides = this.slideshow?.slides;
    if (!mediaId || !slides?.length) return -1;

    return slides.findIndex((slide) => {
      const media = slide.querySelector('.product-media[data-media-id]');
      return media?.getAttribute('data-media-id') === mediaId;
    });
  }

  #getSelectedProductMediaIndex() {
    const selectedMediaId = this.dataset.selectedMediaId;
    const selectedIndex = this.#getProductMediaIndex(selectedMediaId || '');
    return selectedIndex >= 0 ? selectedIndex : 0;
  }

  #selectSelectedProductMedia() {
    const selectedIndex = this.#getSelectedProductMediaIndex();
    Promise.resolve(this.slideshow?.select(selectedIndex, undefined, { animate: false })).then(this.#syncGalleryNav);
  }

  #syncEngravingPreviewFromSelector() {
    const sectionRoot = this.closest('.product-information, .shopify-section');
    const selector = sectionRoot?.querySelector('special-sale-selector');
    const previewDeal = selector?.dataset.engravingPreviewDeal;
    const previewWallet = selector?.dataset.engravingPreviewWallet;
    const activeWallet =
      (previewDeal && previewWallet
        ? selector?.querySelector(`[data-deal="${previewDeal}"] [data-wallet="${previewWallet}"]`)
        : null) || selector?.querySelector('.deal-card--active [data-wallet="0"]');
    if (!(selector instanceof HTMLElement) || !(activeWallet instanceof HTMLElement)) return;

    const enabledInput = activeWallet.querySelector('[data-personalisation-toggle-wallet][value="on"]');
    const textInput = activeWallet.querySelector('[data-personalisation-text-wallet]');

    this.#renderEngravingPreview({
      enabled: enabledInput instanceof HTMLInputElement && enabledInput.checked,
      text: textInput instanceof HTMLInputElement ? textInput.value : '',
      x: Number(selector.dataset.engravingPreviewX) || 50,
      y: Number(selector.dataset.engravingPreviewY) || 70,
      fontSize: Number(selector.dataset.engravingPreviewFontSize) || 22,
      color: selector.dataset.engravingPreviewColor || '#e1dcd2eb',
    });
  }

  #renderEngravingPreview(detail) {
    this.querySelectorAll('.product-media--engraving-preview').forEach((media) => {
      media.classList.remove('product-media--engraving-preview');
      media.style.removeProperty('--engraving-preview-x');
      media.style.removeProperty('--engraving-preview-y');
      media.style.removeProperty('--engraving-preview-font-size');
      media.style.removeProperty('--engraving-preview-color');
    });

    const previewMedia = this.querySelector('[data-engraving-preview-media]');
    const previewText = previewMedia?.querySelector('[data-engraving-preview-text]');

    if (previewMedia instanceof HTMLElement && previewText instanceof HTMLElement) {
      const enabled = Boolean(detail?.enabled);
      this.dataset.engravingPreviewEnabled = String(enabled);
      previewMedia.classList.toggle('product-media--engraving-preview', enabled);
      previewMedia.style.setProperty('--engraving-preview-x', `${detail?.x || 50}%`);
      previewMedia.style.setProperty('--engraving-preview-y', `${detail?.y || 31}%`);
      previewMedia.style.setProperty('--engraving-preview-font-size', `${detail?.fontSize || 13}px`);
      previewMedia.style.setProperty('--engraving-preview-color', detail?.color || '#e1dcd2eb');
      previewText.textContent = enabled ? detail.text || '' : '';

      const previewSlide = previewMedia.closest('slideshow-slide');
      const previewThumbnails = this.querySelectorAll('[data-engraving-preview-thumbnail]');

      if (previewSlide instanceof HTMLElement && enabled) {
        previewSlide.removeAttribute('hidden');
        previewSlide.removeAttribute('reveal');
        previewThumbnails.forEach((thumbnail) => thumbnail.removeAttribute('hidden'));

        this.#engravingPreviewResizeObserver.disconnect();
        this.#engravingPreviewResizeObserver.observe(previewMedia);
        this.#scheduleEngravingPreviewFit(previewText);

        const previewIndex = this.slideshow?.slides?.indexOf(previewSlide);
        if (previewIndex !== undefined && previewIndex >= 0) {
          Promise.resolve(this.slideshow?.select(previewIndex, undefined, { animate: false })).then(this.#syncGalleryNav);
        }
      } else if (previewSlide instanceof HTMLElement) {
        this.#engravingPreviewResizeObserver.disconnect();
        previewThumbnails.forEach((thumbnail) => thumbnail.setAttribute('hidden', ''));

        const selectedMediaIndex = this.#getSelectedProductMediaIndex();
        Promise.resolve(this.slideshow?.select(selectedMediaIndex, undefined, { animate: false })).finally(() => {
          if (this.dataset.engravingPreviewEnabled === 'true') return;
          previewSlide.setAttribute('hidden', '');
          previewSlide.removeAttribute('reveal');
          previewSlide.setAttribute('aria-hidden', 'true');
          this.#syncGalleryNav();
        });
      }
    }

    if (detail?.scrollToGallery) {
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /** @param {HTMLElement} previewText */
  #scheduleEngravingPreviewFit(previewText) {
    cancelAnimationFrame(this.#engravingPreviewFitFrame);
    previewText.style.removeProperty('font-size');
    this.#engravingPreviewFitFrame = requestAnimationFrame(() => this.#fitEngravingPreviewText(previewText));
  }

  /** @param {HTMLElement} previewText */
  #fitEngravingPreviewText(previewText) {
    if (!previewText.isConnected || !previewText.textContent) return;

    const computedStyle = getComputedStyle(previewText);
    const maximumSize = Number.parseFloat(computedStyle.fontSize);
    if (!Number.isFinite(maximumSize)) return;

    const fitsTwoLines = (fontSize) => {
      previewText.style.fontSize = `${fontSize}px`;
      return previewText.scrollHeight <= fontSize * 2.2 + 1;
    };

    if (fitsTwoLines(maximumSize)) return;

    let minimumSize = 8;
    let maximumTestSize = maximumSize;
    for (let index = 0; index < 8; index += 1) {
      const testSize = (minimumSize + maximumTestSize) / 2;
      if (fitsTwoLines(testSize)) {
        minimumSize = testSize;
      } else {
        maximumTestSize = testSize;
      }
    }

    previewText.style.fontSize = `${minimumSize}px`;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #syncGalleryNav = () => {
    const slideshow = this.slideshow;
    const slides = slideshow?.slides;
    if (!slides?.length) return;

    const index = slideshow.current;
    const lastIndex = slides.length - 1;
    const { previous, next } = this.#getGalleryNavButtons();

    if (previous instanceof HTMLButtonElement) {
      const atStart = index <= 0;
      previous.disabled = atStart;
      previous.setAttribute('aria-disabled', String(atStart));
    }

    if (next instanceof HTMLButtonElement) {
      const atEnd = index >= lastIndex;
      next.disabled = atEnd;
      next.setAttribute('aria-disabled', String(atEnd));
    }
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
